import { useMemo, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useCurrentCurrency } from "../../hooks/useCurrentCurrency";
import { useConsolidated } from "../../hooks/useConsolidated";
import { useReceivablesSummaryQuery, useReceivablesGroupAgingBreakdownQuery } from "../../api/queries/receivablesQueries";
import { useOrgUsersQuery } from "../../api/queries/orgUsersQueries";
import { useCustomerGroupsQuery } from "../../api/queries/customerGroupsQueries";
import { resolveReceivablesChartsMetrics } from "../../utils/receivablesChartsMetrics";
import { ReceivablesHeaderToolbar } from "../receivables/ReceivablesHeaderToolbar";
import { ReceivablesChartsPanel } from "../receivables/ReceivablesChartsPanel";
import { DashboardTopDebtorsChart } from "./DashboardTopDebtorsChart";
import { DashboardInvoicedVsCollectedChart } from "./DashboardInvoicedVsCollectedChart";
import { DashboardEmailStatsSection } from "./DashboardEmailStatsSection";
import { DashboardZone } from "./DashboardZone";
import { CashAssistantPanel } from "../cashChat/CashAssistantPanel";
import { MyDashboardSection } from "../cashChat/MyDashboardSection";
import { PieChartEmptyCard } from "../datamintEmptyStates";
import {
  PERIOD_GROUPING_OPTIONS,
  type PeriodGrouping,
} from "../../utils/periodGrouping";

function formatYmdLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function buildDefaultDateRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), 0, 1);
  return {
    startDate: formatYmdLocal(start),
    endDate: formatYmdLocal(end),
  };
}

export function DashboardReceivablesPanel() {
  const { scopes, isOrgManager, isOrgCollector, hasFeature } = useAuth();
  const hasReceivables = scopes?.includes("RECEIVABLES") ?? false;
  const showGroupAgingBreakdown = hasFeature("RECEIVABLES_GROUP_AGING_BREAKDOWN");

  const { currency } = useCurrentCurrency();
  const { effectiveConsolidated } = useConsolidated();

  const defaultDates = useMemo(() => buildDefaultDateRange(), []);
  const [startDate, setStartDate] = useState<string | undefined>(
    defaultDates.startDate,
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    defaultDates.endDate,
  );

  const [filterCollectorId, setFilterCollectorId] = useState<number | "">("");
  const [filterGroupId, setFilterGroupId] = useState<number | "">("");
  // Period granularity is shared by every time-series card below, so it
  // lives in the one filter row rather than inside a chart.
  const [grouping, setGrouping] = useState<PeriodGrouping>("month");

  const { data: orgUsersData } = useOrgUsersQuery({ enabled: isOrgManager });
  const { data: customerGroups = [] } = useCustomerGroupsQuery({
    enabled: isOrgManager || isOrgCollector,
  });

  const managerFilters = useMemo(() => {
    const customerGroupId =
      showGroupAgingBreakdown || filterGroupId === ""
        ? undefined
        : filterGroupId;
    if (isOrgManager) {
      return {
        collectorId: filterCollectorId === "" ? undefined : filterCollectorId,
        customerGroupId,
      };
    }
    if (isOrgCollector && customerGroupId != null) {
      return { customerGroupId };
    }
    return undefined;
  }, [
    isOrgManager,
    isOrgCollector,
    filterCollectorId,
    filterGroupId,
    showGroupAgingBreakdown,
  ]);

  const summaryQuery = useReceivablesSummaryQuery(
    currency,
    startDate,
    endDate,
    managerFilters,
    effectiveConsolidated,
  );

  const groupAgingBreakdownQuery = useReceivablesGroupAgingBreakdownQuery({
    currency,
    consolidated: effectiveConsolidated,
    managerFilters,
    enabled: showGroupAgingBreakdown,
  });

  const criticalAmount = summaryQuery.data?.criticalAmount ?? 0;
  const criticalInvoicesCount = summaryQuery.data?.criticalInvoicesCount ?? 0;
  const unpaidInvoicesCount = summaryQuery.data?.unpaidInvoicesCount ?? 0;
  const receivablesDisplayCurrency = summaryQuery.data?.currency ?? currency;

  const {
    agingBuckets,
    openBalanceAmount,
    criticalAmount: chartsCriticalAmount,
    criticalInvoicesCount: chartsCriticalInvoicesCount,
  } = resolveReceivablesChartsMetrics(
    summaryQuery.data
      ? {
          agingBuckets: summaryQuery.data.agingBuckets,
          unscopedAgingBuckets: summaryQuery.data.unscopedAgingBuckets,
          unscopedOpenBalance: summaryQuery.data.unscopedOpenBalance,
          unscopedCriticalAmount: summaryQuery.data.unscopedCriticalAmount,
          unscopedCriticalInvoiceCount:
            summaryQuery.data.unscopedCriticalInvoiceCount,
          criticalAmount,
          criticalInvoicesCount,
        }
      : undefined,
  );

  if (!hasReceivables) {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col justify-center px-6 py-8">
        <PieChartEmptyCard title="Receivables" ctaTo="/files-integrations" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-2">
      <div className="sticky top-0 z-20 -mx-1 border-b border-[#E5E7EB] bg-[#FAFAFA]/95 px-1 py-3 backdrop-blur-sm">
        <div className="flex flex-col gap-3 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 shadow-sm lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-[#111827]">
              Receivables
            </h1>
            <p className="mt-0.5 text-sm text-[#6B7280]">
              Aging, collection performance, and outstanding balances
            </p>
          </div>
          <ReceivablesHeaderToolbar
            showCollectorFilter={isOrgManager}
            showGroupFilter={
              !showGroupAgingBreakdown &&
              (isOrgManager || (isOrgCollector && customerGroups.length > 0))
            }
            filterCollectorId={filterCollectorId}
            onFilterCollectorIdChange={setFilterCollectorId}
            collectors={(orgUsersData?.members ?? [])
              .filter((m) => m.org_role === "COLLECTOR")
              .map((m) => ({ id: m.id, username: m.username }))}
            filterGroupId={filterGroupId}
            onFilterGroupIdChange={setFilterGroupId}
            groups={customerGroups.map((g) => ({ id: g.id, name: g.name }))}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={(from, to) => {
              setStartDate(from);
              setEndDate(to);
            }}
          />
        </div>

        {/* One granularity control for every time-series card below —
            filters live above what they scope, never inside a chart. */}
        <div className="mt-2 flex flex-wrap items-center gap-2 px-1">
          <span className="text-xs font-medium text-[#6B7280]">Group by</span>
          <div
            role="radiogroup"
            aria-label="Period grouping"
            className="inline-flex rounded-lg border border-[#E5E7EB] bg-white p-0.5"
          >
            {PERIOD_GROUPING_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={grouping === option.value}
                onClick={() => setGrouping(option.value)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  grouping === option.value
                    ? "bg-[#F3F4F6] text-[#111827]"
                    : "text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {summaryQuery.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-8 text-center"
        >
          <p className="text-sm text-[#DC2626]">
            Failed to load receivables summary.
          </p>
          <button
            type="button"
            onClick={() => void summaryQuery.refetch()}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          >
            Try again
          </button>
        </div>
      ) : (
        <ReceivablesChartsPanel
          currency={currency}
          consolidated={effectiveConsolidated}
          startDate={startDate}
          endDate={endDate}
          managerFilters={managerFilters}
          receivablesDisplayCurrency={receivablesDisplayCurrency}
          unpaidInvoicesCount={unpaidInvoicesCount}
          openBalanceAmount={openBalanceAmount}
          criticalAmount={chartsCriticalAmount}
          criticalInvoicesCount={chartsCriticalInvoicesCount}
          agingBuckets={agingBuckets}
          grouping={grouping}
          groupAgingBreakdowns={
            showGroupAgingBreakdown
              ? groupAgingBreakdownQuery.data
              : undefined
          }
        />
      )}

      {/* DSO (DashboardDsoMonthlyChart) and Cash collected (DashboardCashCollectedChart) hidden until rollout — see git history / PR #615 */}

      <DashboardZone
        title="Billing and collection"
        description="What was invoiced against what actually came in (by issue date)"
      >
        <DashboardInvoicedVsCollectedChart
          currency={receivablesDisplayCurrency}
          startDate={startDate}
          endDate={endDate}
          grouping={grouping}
          managerFilters={managerFilters}
          consolidated={effectiveConsolidated}
        />
      </DashboardZone>

      <DashboardZone
        title="Concentration"
        description="Where the outstanding balance actually sits"
      >
        <DashboardTopDebtorsChart
          currency={receivablesDisplayCurrency}
          consolidated={effectiveConsolidated}
          startDate={startDate}
          endDate={endDate}
          managerFilters={managerFilters}
        />
      </DashboardZone>

      <DashboardZone
        title="Outreach performance"
        description="Outgoing email volume and reply effectiveness"
      >
        <DashboardEmailStatsSection
          startDate={startDate}
          endDate={endDate}
          managerFilters={managerFilters}
          embedded
        />
      </DashboardZone>

      <DashboardZone
        title="My charts"
        description="Charts you pinned from the assistant"
      >
        <MyDashboardSection />
      </DashboardZone>

      <CashAssistantPanel />
    </div>
  );
}
